import React, { useEffect, useState } from "react";
import { Card, Badge, Modal, Button } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import axios from "axios";

const SalesChart = () => {
  const [weekData, setWeekData] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 767 : false,
  );

  useEffect(() => {
    const fetchData = () => {
      axios
        .get("./dati_dashboard.json")
        .then((res) => {
          const weekOrdersData = res.data.TP.settimana || [];

          const sortedWeekData = [...weekOrdersData].sort(
            (a, b) =>
              Number(a.ordine_giorno) - Number(b.ordine_giorno),
          );

          setWeekData(sortedWeekData);
        })
        .catch((err) => console.error(err));
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleToggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const renderCustomLegend = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "14px",
      paddingTop: "8px",
      fontSize: "10px",
      fontWeight: "700",
      color: "#b8bbc2",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#b81f25",
          flexShrink: 0,
        }}
      />

      <span>Ordinato</span>
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
      }}
    >
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          backgroundColor: "#4b4b4b",
          flexShrink: 0,
        }}
      />

      <span>Media (6m)</span>
    </div>
  </div>
);

  /* =========================================================
     GIORNO CORRENTE
     Lunedì = 1
     ...
     Domenica = 7
     ========================================================= */

  const todayJs = new Date().getDay();
  const currentDayIndex = todayJs === 0 ? 7 : todayJs;

  const yesterdayIndex =
    currentDayIndex === 1 ? 7 : currentDayIndex - 1;

  const tomorrowIndex =
    currentDayIndex === 7 ? 1 : currentDayIndex + 1;

  /* =========================================================
     DATI MOBILE
     Solo IERI / OGGI / DOMANI
     ========================================================= */

  const getMobileData = () => {
    const wantedDays = [
      {
        order: yesterdayIndex,
        mobileLabel: "IERI",
      },
      {
        order: currentDayIndex,
        mobileLabel: "OGGI",
      },
      {
        order: tomorrowIndex,
        mobileLabel: "DOMANI",
      },
    ];

    return wantedDays
      .map(({ order, mobileLabel }) => {
        const found = weekData.find(
          (item) => Number(item.ordine_giorno) === order,
        );

        if (!found) return null;

        return {
          ...found,
          mobileLabel,
        };
      })
      .filter(Boolean);
  };

  const formatToEuro = (amount) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  /* =========================================================
     TREND SOPRA LE BARRE
     ========================================================= */

  const renderTrendLabel = (chartData) => (props) => {
    const { x, y, width, index } = props;

    const dataItem = chartData[index];

    if (!dataItem) return null;

    const dayOrder = Number(dataItem.ordine_giorno);

    // Sul giorno di domani non mostriamo il trend.
    if (dayOrder === tomorrowIndex) {
      return null;
    }

    const valore = parseFloat(dataItem.valore_ordinato);
    const media = parseFloat(dataItem.media_valore);

    if (!Number.isFinite(valore) || !Number.isFinite(media) || media === 0) {
      return null;
    }

    const trend = ((valore - media) / media) * 100;
    const color = trend >= 0 ? "#28a745" : "#dc3545";

    return (
      <text
        x={x + width / 2}
        y={y - 12}
        fill={color}
        textAnchor="middle"
        style={{
          fontSize: isMobile ? "9px" : "10px",
          fontWeight: "800",
        }}
      >
        {trend >= 0 ? "▲" : "▼"} {Math.abs(trend).toFixed(1)}%
      </text>
    );
  };

  /* =========================================================
     GRAFICO
     ========================================================= */

  const renderChart = (isFull = false) => {
    const chartData =
      isMobile && !isFull ? getMobileData() : weekData;

    const maxYValue =
      Math.max(
        0,
        ...chartData.map((d) =>
          Math.max(
            Number(d.valore_ordinato) || 0,
            Number(d.media_valore) || 0,
          ),
        ),
      ) * 1.15;

    return (
      <ResponsiveContainer
        width="100%"
        height={isFull ? 500 : "100%"}
      >
        <BarChart
          data={chartData}
          margin={{
            top: 30,
            right: 10,
            left: 0,
            bottom: 0,
          }}
          barGap={isMobile && !isFull ? 10 : 8}
        >
          <XAxis
            dataKey={
              isMobile && !isFull
                ? "mobileLabel"
                : "it_nome_giorno"
            }
            axisLine={false}
            tickLine={false}
            tick={{
              fill: "#9ca0a8",
              fontSize: isMobile ? 10 : 11,
              fontWeight: "700",
            }}
            tickFormatter={(value) => {
              if (isMobile && !isFull) {
                return value;
              }

              return String(value)
                .substring(0, 3)
                .toUpperCase();
            }}
          />

          <YAxis
            hide
            domain={[0, maxYValue || "auto"]}
          />

          <Tooltip
            cursor={{
              fill: "rgba(255,255,255,0.025)",
            }}
            contentStyle={{
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "rgba(20,22,27,0.96)",
              color: "#fff",
              boxShadow: "0 10px 30px rgba(0,0,0,0.28)",
              fontSize: "12px",
            }}
            labelStyle={{
              color: "#fff",
              fontWeight: "700",
            }}
            formatter={(value) => [
              formatToEuro(value),
              "",
            ]}
          />

         <Legend content={renderCustomLegend} />

          <Bar
            dataKey="valore_ordinato"
            name="Ordinato"

            // 🔥 Serve anche per dare alla LEGEND il colore corretto
            fill="#b81f25"

            radius={[6, 6, 0, 0]}
            barSize={
              isFull
                ? 60
                : isMobile
                  ? 36
                  : 40
            }
          >
            <LabelList
              content={renderTrendLabel(chartData)}
            />

            {chartData.map((entry, index) => {
              const day = Number(entry.ordine_giorno);

              const isFuture =
                day === tomorrowIndex;

              return (
                <Cell
                  key={`cell-v-${index}`}
                  fill="#b81f25"
                  fillOpacity={isFuture ? 0.22 : 1}
                />
              );
            })}
          </Bar>

          <Bar
            dataKey="media_valore"
            name="Media (6m)"

            // 🔥 Colore corretto anche nella legenda
            fill="#4b4b4b"

            radius={[6, 6, 0, 0]}
            barSize={
              isFull
                ? 60
                : isMobile
                  ? 36
                  : 40
            }
          >
            {chartData.map((entry, index) => {
              const day = Number(entry.ordine_giorno);

              const isFuture =
                day === tomorrowIndex;

              return (
                <Cell
                  key={`cell-m-${index}`}
                  fill="#4b4b4b"
                  fillOpacity={isFuture ? 0.22 : 0.8}
                />
              );
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <>
      {/* =====================================================
          CARD PRINCIPALE
          ===================================================== */}

      <Card
        className="
          card
          ombra
          card-week
          card-home
          hover-effect
          border-0
          tp-chart-card
        "
        style={{
          borderRadius: "15px",
          cursor: "pointer",
        }}
        onClick={handleToggleModal}
      >
        <div
          className="
            d-flex
            justify-content-between
            align-items-center
            px-3
            tp-chart-header
          "
        >
          <div>
            <h6
              className="text-title fw-bold mb-0"
              style={{
                letterSpacing: "1px",
              }}
            >
              PERFORMANCE SETTIMANALE
            </h6>

            <small
              className="text-muted"
              style={{
                fontSize: "0.65rem",
              }}
            >
              Clicca per espandere
            </small>
          </div>

          <Badge
            bg="danger"
            pill
            style={{
              fontSize: "0.6rem",
              padding: "5px 10px",
            }}
          >
            WEEKLY
          </Badge>
        </div>

        <div className="tp-chart-body">
          {renderChart(false)}
        </div>
      </Card>

      {/* =====================================================
          MODALE
          ===================================================== */}

      <Modal
        show={showModal}
        onHide={handleToggleModal}
        size="xl"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header
          closeButton
          className="border-0 pb-0"
        >
          <Modal.Title className="fw-bold text-title">
            Dettaglio Performance Settimanale
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="pt-0">
          <p className="text-muted mb-4 px-1">
            Analisi comparativa del valore ordinato rispetto alla
            media semestrale.
          </p>

          <div
            style={{
              width: "100%",
              padding: "20px",
            }}
          >
            {renderChart(true)}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0">
          <Button
            variant="secondary"
            onClick={handleToggleModal}
          >
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SalesChart;